import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronDown, Heart, Play } from "lucide-react";
import {
  Container, PageLayout, Headline, MutedText, Button, BackButton, SeedTag, Card,
} from "../components/ui";
import { Cover } from "../components/Cover";
import { seedLabel } from "../lib/seedLabel";
import { getStory, setFavorite } from "../lib/dataSource";
import { useAudioPlayer } from "../lib/audioPlayer";
import { svgCoverToDataUrl } from "../lib/coverArtwork";
import type { StoryDetail as StoryDetailType } from "../lib/types";

function formatMinutes(seconds: number): string {
  return `${Math.round(seconds / 60)}`;
}

export default function StoryDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { play } = useAudioPlayer();
  const [story, setStory] = useState<StoryDetailType | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [truthOpen, setTruthOpen] = useState(false);
  const [parentNoteOpen, setParentNoteOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    setStory(null);
    setNotFound(false);
    getStory(id)
      .then(setStory)
      .catch(() => setNotFound(true));
  }, [id]);

  function handleListen() {
    if (!story?.audio_url) return;
    play({
      id: story.id,
      url: story.audio_url,
      title: story.title,
      subtitle: story.child ? t("story.forChild", { name: story.child.first_name }) : undefined,
      coverSvg: story.cover_svg,
      coverImageUrl: story.cover_image_url,
      coverImageThumbUrl: story.cover_image_thumb_url,
      coverColorway: story.cover_colorway,
      artwork: svgCoverToDataUrl(story.cover_svg),
      chapters: story.chapters,
      kind: story.kind,
    });
    navigate("/listen");
  }

  // Optimiste : l'icône bascule avant la réponse serveur, et revient en
  // arrière seule en cas d'échec — pas de round-trip visible pour un geste
  // aussi anodin qu'un cœur.
  async function handleToggleFavorite() {
    if (!story) return;
    const next = !story.is_favorite;
    setStory({ ...story, is_favorite: next });
    try {
      await setFavorite(story.id, next);
    } catch {
      setStory((s) => (s ? { ...s, is_favorite: !next } : s));
    }
  }

  if (notFound) {
    return (
      <PageLayout>
        <Container className="pt-8">
          <MutedText>{t("story.notFound")}</MutedText>
        </Container>
      </PageLayout>
    );
  }

  if (!story) {
    return (
      <PageLayout>
        <Container className="pt-8">
          <MutedText>{t("common.loading")}</MutedText>
        </Container>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container className="pt-6 flex flex-col gap-5">
        <BackButton onClick={() => navigate(-1)} />

        <div className="flex flex-col items-center text-center gap-3">
          <div className="w-44 sm:w-56">
            <Cover
              svg={story.cover_svg}
              imageUrl={story.cover_image_url}
              imageThumbUrl={story.cover_image_thumb_url}
              imageSize="full"
              title={story.title}
              colorway={story.cover_colorway}
              colorKey={story.slug}
            />
          </div>
          {story.child && (
            <p className="eyebrow">{t("story.forChild", { name: story.child.first_name })}</p>
          )}
          <Headline className="text-2xl">{story.title}</Headline>
          <div className="flex flex-col items-center gap-2">
            <SeedTag>{seedLabel(story.seed)}</SeedTag>
            <span className="text-xs text-violet-soft font-heading font-semibold">
              {formatMinutes(story.duration_s)} {t("common.minutes")}, {story.age_min === story.age_max ? t("story.ageSingle", { age: story.age_min }) : t("story.ageRange", { min: story.age_min, max: story.age_max })}
            </span>
          </div>
          <MutedText className="max-w-prose">{story.synopsis}</MutedText>
        </div>

        {/* Écouter + favori sur une seule ligne : le cœur est un geste
            anodin, il n'a pas à occuper une rangée entière. Carré, de la
            même hauteur que « Écouter » (py-4 + icône) grâce à
            `aspect-square` et au `self-stretch` implicite du flex. */}
        <div className="flex items-stretch gap-3">
          <Button onClick={handleListen} disabled={!story.audio_url} className="flex-1 text-base py-4">
            <Play size={18} fill="currentColor" />
            {t("story.listen")}
          </Button>
          <Button
            variant="secondary"
            onClick={() => void handleToggleFavorite()}
            className="aspect-square px-0 py-0"
            aria-label={story.is_favorite ? t("story.unfavorite") : t("story.favorite")}
          >
            <Heart size={20} className={story.is_favorite ? "text-coral" : undefined} fill={story.is_favorite ? "currentColor" : "none"} />
          </Button>
        </div>

        {story.chapters.length > 0 && (
          <Card>
            <h2 className="font-heading font-bold text-sm mb-2">{t("story.chapters")}</h2>
            <ul className="flex flex-col gap-1.5">
              {story.chapters.map((c) => (
                <li key={c.anchor ?? c.title} className="text-sm text-violet-soft font-body flex justify-between">
                  <span>{c.title}</span>
                  <span>{Math.round(c.start_s / 60)}′</span>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {story.truth_check && (
          <Card>
            <button
              type="button"
              className="flex w-full items-center justify-between font-heading font-bold text-sm"
              onClick={() => setTruthOpen((v) => !v)}
              aria-expanded={truthOpen}
            >
              {t("story.truthCheck")}
              <ChevronDown size={16} className={`transition-transform ${truthOpen ? "rotate-180" : ""}`} />
            </button>
            {truthOpen && <MutedText className="mt-2">{story.truth_check}</MutedText>}
          </Card>
        )}

        {story.parent_note && (
          <Card>
            <button
              type="button"
              className="flex w-full items-center justify-between font-heading font-bold text-sm"
              onClick={() => setParentNoteOpen((v) => !v)}
              aria-expanded={parentNoteOpen}
            >
              {t("story.parentNote")}
              <ChevronDown size={16} className={`transition-transform ${parentNoteOpen ? "rotate-180" : ""}`} />
            </button>
            {parentNoteOpen && <MutedText className="mt-2">{story.parent_note}</MutedText>}
          </Card>
        )}
      </Container>
    </PageLayout>
  );
}
